import {
  collectionGroup,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  orderBy,
  limit,
  documentId,
  collection,
} from "firebase/firestore";
import { db } from "../config/firebase";
import type { Event } from "../types/event.types";

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const getBarNames = async (barIds: string[]): Promise<Map<string, string>> => {
  const names = new Map<string, string>();
  const uniqueBarIds = [...new Set(barIds.filter(Boolean))];

  for (const batch of chunkArray(uniqueBarIds, 10)) {
    const barsSnapshot = await getDocs(
      query(collection(db, "bars"), where(documentId(), "in", batch)),
    );

    barsSnapshot.docs.forEach((barDoc) => {
      names.set(barDoc.id, (barDoc.data().name as string) || barDoc.id);
    });
  }

  return names;
};

export interface EventWithBar extends Event {
  barName: string;
}

export interface EventStats {
  totalEvents: number;
  upcomingEvents: number;
  ongoingEvents: number;
  completedEvents: number;
  cancelledEvents: number;
}

/**
 * Lädt alle Events über alle Bars
 */
export const getAllEvents = async (): Promise<EventWithBar[]> => {
  try {
    const eventsQuery = query(
      collectionGroup(db, "events"),
      orderBy("startDateTime", "desc"),
      limit(100),
    );

    const snapshot = await getDocs(eventsQuery);
    const events: EventWithBar[] = [];
    const barIds = snapshot.docs
      .map((eventDoc) => eventDoc.ref.parent.parent?.id || "")
      .filter(Boolean);
    const barNames = await getBarNames(barIds);

    for (const eventDoc of snapshot.docs) {
      const eventData = eventDoc.data();
      const barId = eventDoc.ref.parent.parent?.id || "";
      const barName = barNames.get(barId) || "Unknown Bar";

      events.push({
        id: eventDoc.id,
        barId,
        barName,
        title: eventData.title,
        description: eventData.description,
        type: eventData.type,
        startDateTime: eventData.startDateTime,
        endDateTime: eventData.endDateTime,
        imageUrl: eventData.imageUrl,
        images: eventData.images || [],
        // Harmonized fields (read both old + new names)
        maxCapacity: eventData.maxCapacity ?? eventData.maxAttendees,
        attendeeCount:
          eventData.attendeeCount ?? eventData.currentAttendees ?? 0,
        interestedCount: eventData.interestedCount ?? 0,
        entryFee: eventData.entryFee ?? eventData.ticketPrice,
        currency: eventData.currency ?? "EUR",
        viewCount: eventData.viewCount ?? 0,
        status: eventData.status || "scheduled",
        isPublished: eventData.isPublished !== false,
        isRecurring: eventData.isRecurring ?? false,
        sportMetadata: eventData.sportMetadata,
        createdBy: eventData.createdBy || "",
        createdAt: eventData.createdAt,
        updatedAt: eventData.updatedAt || eventData.createdAt,
      });
    }

    return events;
  } catch (error) {
    console.error("Error fetching all events:", error);
    throw error;
  }
};

/**
 * Lädt Events für eine bestimmte Bar
 */
export const getEventsByBar = async (
  barId: string,
): Promise<EventWithBar[]> => {
  try {
    const allEvents = await getAllEvents();
    return allEvents.filter((event) => event.barId === barId);
  } catch (error) {
    console.error("Error fetching events by bar:", error);
    throw error;
  }
};

/**
 * Lädt alle Bars die Events haben (nur geclaimte Bars)
 */
export const getBarsWithEvents = async (): Promise<
  Array<{
    barId: string;
    barName: string;
    eventCount: number;
    isClaimed: boolean;
  }>
> => {
  try {
    const allEvents = await getAllEvents();

    // Gruppiere Events nach barId
    const barMap = new Map<string, { barName: string; count: number }>();

    for (const event of allEvents) {
      if (event.barId) {
        const existing = barMap.get(event.barId);
        if (existing) {
          existing.count++;
        } else {
          barMap.set(event.barId, { barName: event.barName, count: 1 });
        }
      }
    }

    // Konvertiere zu Array und prüfe isClaimed
    const barIds = [...barMap.keys()];
    const claimedBars = new Set<string>();

    for (const batch of chunkArray(barIds, 10)) {
      const barsSnapshot = await getDocs(
        query(collection(db, "bars"), where(documentId(), "in", batch)),
      );

      barsSnapshot.docs.forEach((barDoc) => {
        if (barDoc.data().isClaimed === true) {
          claimedBars.add(barDoc.id);
        }
      });
    }

    const barsWithEvents = [];
    for (const [barId, data] of barMap.entries()) {
      if (claimedBars.has(barId)) {
        barsWithEvents.push({
          barId,
          barName: data.barName,
          eventCount: data.count,
          isClaimed: true,
        });
      }
    }

    return barsWithEvents.sort((a, b) => b.eventCount - a.eventCount);
  } catch (error) {
    console.error("Error fetching bars with events:", error);
    throw error;
  }
};

/**
 * Lädt Events nach Status-Filter
 */
export const getEventsByStatus = async (
  status: "upcoming" | "ongoing" | "completed" | "cancelled",
): Promise<EventWithBar[]> => {
  try {
    const eventsQuery = query(
      collectionGroup(db, "events"),
      where("status", "==", status),
      orderBy("startDateTime", "desc"),
      limit(100),
    );

    const snapshot = await getDocs(eventsQuery);
    const events: EventWithBar[] = [];
    const barIds = snapshot.docs
      .map((eventDoc) => eventDoc.ref.parent.parent?.id || "")
      .filter(Boolean);
    const barNames = await getBarNames(barIds);

    for (const eventDoc of snapshot.docs) {
      const eventData = eventDoc.data();
      const barId = eventDoc.ref.parent.parent?.id || "";
      const barName = barNames.get(barId) || "Unknown Bar";

      events.push({
        id: eventDoc.id,
        barId,
        barName,
        title: eventData.title,
        description: eventData.description,
        type: eventData.type,
        startDateTime: eventData.startDateTime,
        endDateTime: eventData.endDateTime,
        imageUrl: eventData.imageUrl,
        images: eventData.images || [],
        maxCapacity: eventData.maxCapacity ?? eventData.maxAttendees,
        attendeeCount:
          eventData.attendeeCount ?? eventData.currentAttendees ?? 0,
        interestedCount: eventData.interestedCount ?? 0,
        entryFee: eventData.entryFee ?? eventData.ticketPrice,
        currency: eventData.currency ?? "EUR",
        viewCount: eventData.viewCount ?? 0,
        status: eventData.status || "scheduled",
        isPublished: eventData.isPublished !== false,
        isRecurring: eventData.isRecurring ?? false,
        sportMetadata: eventData.sportMetadata,
        createdBy: eventData.createdBy || "",
        createdAt: eventData.createdAt,
        updatedAt: eventData.updatedAt || eventData.createdAt,
      });
    }

    return events;
  } catch (error) {
    console.error("Error fetching events by status:", error);
    throw error;
  }
};

/**
 * Aktualisiert Event-Status
 */
export const updateEventStatus = async (
  barId: string,
  eventId: string,
  status: "upcoming" | "ongoing" | "completed" | "cancelled",
): Promise<void> => {
  try {
    const eventRef = doc(db, "bars", barId, "events", eventId);
    await updateDoc(eventRef, {
      status,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error updating event status:", error);
    throw error;
  }
};

/**
 * Pausiert/Unpublisht ein Event
 */
export const toggleEventPublish = async (
  barId: string,
  eventId: string,
  isPublished: boolean,
): Promise<void> => {
  try {
    const eventRef = doc(db, "bars", barId, "events", eventId);
    await updateDoc(eventRef, {
      isPublished,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error toggling event publish:", error);
    throw error;
  }
};

/**
 * Löscht ein Event
 */
export const deleteEvent = async (
  barId: string,
  eventId: string,
): Promise<void> => {
  try {
    const eventRef = doc(db, "bars", barId, "events", eventId);
    await deleteDoc(eventRef);
  } catch (error) {
    console.error("Error deleting event:", error);
    throw error;
  }
};

/**
 * Lädt Event Stats
 */
export const getEventStats = async (): Promise<EventStats> => {
  try {
    const now = Timestamp.now();
    const allEventsQuery = query(collectionGroup(db, "events"));
    const allEventsSnapshot = await getDocs(allEventsQuery);

    let upcomingEvents = 0;
    let ongoingEvents = 0;
    let completedEvents = 0;
    let cancelledEvents = 0;

    allEventsSnapshot.docs.forEach((eventDoc) => {
      const eventData = eventDoc.data();
      const status = eventData.status || "upcoming";

      // Automatischer Status basierend auf Datum
      const startDate = eventData.startDateTime?.toDate();
      const endDate = eventData.endDateTime?.toDate();

      if (status === "cancelled") {
        cancelledEvents++;
      } else if (endDate && endDate < now.toDate()) {
        completedEvents++;
      } else if (
        startDate &&
        endDate &&
        startDate <= now.toDate() &&
        endDate >= now.toDate()
      ) {
        ongoingEvents++;
      } else {
        upcomingEvents++;
      }
    });

    return {
      totalEvents: allEventsSnapshot.size,
      upcomingEvents,
      ongoingEvents,
      completedEvents,
      cancelledEvents,
    };
  } catch (error) {
    console.error("Error fetching event stats:", error);
    return {
      totalEvents: 0,
      upcomingEvents: 0,
      ongoingEvents: 0,
      completedEvents: 0,
      cancelledEvents: 0,
    };
  }
};
